import Axios, { AxiosRequestConfig } from 'axios';
import { useOutlookBearerStore } from '@features/outlook/stores/outlookBearerStore';
import { useKioskSessionStore } from '@features/kiosk/stores/kioskSessionStore';
import {
  TENANT_HEADER,
  isTenantHeaderRejection,
  resolveEffectiveTenant,
  useAdminTenantStore,
} from '@app/stores/adminTenantStore';

const BFF_PATHS: Record<string, string> = {
  visitorApi: '/bff/api/v1/visitor-api',
  notifications: '/bff/api/v1/notifications',
  workflow: '/bff/api/v1/workflow',
  files: '/bff/api/v1/files',
  iprotect: '/bff/api/v1/iprotect',
  transconnect: '/bff/api/v1/transconnect',
};

const BFF_ROOT = '';
function createServiceInstance(baseURL: string) {
  const instance = Axios.create({
    baseURL,
    timeout: 30000,
    withCredentials: true, // Include cookies on all requests (BFF session)
  });

  instance.interceptors.request.use(
    (config) => {
      const method = config.method?.toLowerCase() ?? '';
      const bearerToken = useOutlookBearerStore.getState().accessToken;

      if (bearerToken) {
        config.headers.Authorization = `Bearer ${bearerToken}`;
      } else if (config.headers && 'Authorization' in config.headers) {
        delete config.headers.Authorization;
      }

      if (!['get', 'head', 'options'].includes(method)) {
        config.headers['x-csrf'] = '1';
      }

      // Phase 51 D-01: X-Maja-Tenant only from /admin pages, only for a platform_admin, only when
      // the selection differs from the home tenant. Anything else must never carry the header.
      const tenantState = useAdminTenantStore.getState();
      const { headerValue } = resolveEffectiveTenant({
        pathname: window.location.pathname,
        homeTenantId: tenantState.homeTenantId,
        selectedTenantId: tenantState.selectedTenantId,
        isPlatformAdmin: tenantState.isPlatformAdmin,
      });
      if (headerValue) {
        config.headers[TENANT_HEADER] = headerValue;
      } else if (config.headers && TENANT_HEADER in config.headers) {
        delete config.headers[TENANT_HEADER];
      }

      if (config.data && !(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Phase 51 D-03 fallback: the server refused the selected tenant, so go back to the home
      // tenant. 503 TENANT_CHECK_UNAVAILABLE is transient and keeps the selection.
      if (Axios.isAxiosError(error) && isTenantHeaderRejection(error.response?.status, error.response?.data)) {
        useAdminTenantStore.getState().clear();
      }

      if (Axios.isAxiosError(error) && error.response?.status === 401) {
        // /outlook: the task pane authenticates via the Office Dialog popup (G-19-02) — a
        // top-frame redirect trips Keycloak's frame-ancestors CSP.
        // /kiosk: an unattended kiosk has no human to complete an interactive Keycloak login.
        // It authenticates as a DEVICE via POST /bff/kiosk/auth (ROPC), and the routes are
        // declared `publicly accessible, no auth required` in Router.tsx. Redirecting it here
        // strands the device on a login screen it can never satisfy; the calling screen must
        // surface the 401 itself instead.
        //
        // Quick task 260907-hmv: on a /kiosk path, a 401 additionally means the device's own
        // session cannot be renewed (see BFF-side sliding offline-token refresh) — every kiosk
        // API call must produce that device-level consequence, not just the arrival scan, so it
        // is marked here rather than in each individual kiosk screen.
        //
        // Gap closure 42-GAP-02 (residual of 260907-hmv, found during Phase 42 UAT): a 401 is
        // NOT on its own proof the device's session is gone. TokenRefreshMiddleware now answers
        // 401 refresh_failed ONLY for an AUTHORITATIVE refresh rejection (the grant is genuinely
        // dead); a TRANSIENT failure (identity provider briefly unreachable, e.g. mid-Aspire-
        // restart) answers 503 auth_temporarily_unavailable instead and is handled below by
        // simply NOT marking the session expired — the caller keeps its session and the next
        // request (React Query retry, next scan, next poll) naturally retries against it. Any
        // OTHER 401 on a kiosk path (e.g. "no BFF session at all" from an unauthenticated
        // manual-key-entry device) is left to the calling screen exactly as before — it must not
        // force a re-setup either, since markExpired() is reserved for "had a session, it died".
        const path = window.location.pathname;
        const isKioskSurface = path.startsWith('/kiosk');
        const isSelfAuthenticatingSurface = path.startsWith('/outlook') || isKioskSurface;

        const responseData = error.response?.data;
        const errorCode =
          responseData && typeof responseData === 'object' && 'error' in responseData
            ? (responseData as { error?: unknown }).error
            : undefined;
        const isAuthoritativeRefreshFailure = errorCode === 'refresh_failed';

        if (isKioskSurface && isAuthoritativeRefreshFailure) {
          useKioskSessionStore.getState().markExpired();
        }

        if (!isSelfAuthenticatingSurface) {
          const returnUrl = encodeURIComponent(path);
          window.location.href = `/bff/login?returnUrl=${returnUrl}`;
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

type ServiceKey = keyof typeof BFF_PATHS;

function createCustomInstance(serviceKey: ServiceKey) {
  return function customInstance<T>(config: AxiosRequestConfig): Promise<T> {
    const instance = createServiceInstance(BFF_PATHS[serviceKey]);
    const source = Axios.CancelToken.source();
    const promise = instance({
      ...config,
      cancelToken: source.token,
    }).then(({ data }) => data as T);

    // @ts-expect-error - Adding cancel method to promise for react-query
    promise.cancel = () => {
      source.cancel('Query was cancelled');
    };

    return promise;
  };
}

export function notificationsInstance<T>(config: AxiosRequestConfig): Promise<T> {
  return createCustomInstance('notifications')(config);
}

export function workflowInstance<T>(config: AxiosRequestConfig): Promise<T> {
  return createCustomInstance('workflow')(config);
}

export function filesInstance<T>(config: AxiosRequestConfig): Promise<T> {
  return createCustomInstance('files')(config);
}

export function iprotectInstance<T>(config: AxiosRequestConfig): Promise<T> {
  return createCustomInstance('iprotect')(config);
}

export function transconnectInstance<T>(config: AxiosRequestConfig): Promise<T> {
  return createCustomInstance('transconnect')(config);
}

export function visitorApiInstance<T>(config: AxiosRequestConfig): Promise<T> {
  return createCustomInstance('visitorApi')(config);
}

/**
 * BFF-native instance for endpoints the BFF itself implements (not microservice proxies).
 *
 * Use for routes like:
 *   - /bff/user, /bff/login, /bff/logout
 *   - /bff/sessions, /bff/sessions/{id}
 *   - /bff/api/kiosk/start-safety-training (BFF SignalR relay)
 *   - /ready, /health
 *
 * Inherits the same interceptors as service instances (CSRF, credentials, 401 redirect).
 *
 * Note: orval cannot generate clients for these because the BFF does not expose its own
 * OpenAPI spec — only the microservices do. This is the intentional escape hatch.
 */
export function bffInstance<T>(config: AxiosRequestConfig): Promise<T> {
  const instance = createServiceInstance(BFF_ROOT);
  const source = Axios.CancelToken.source();
  const promise = instance({
    ...config,
    cancelToken: source.token,
  }).then(({ data }) => data as T);

  // @ts-expect-error - Adding cancel method to promise for react-query
  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
}

// Dynamic service lookup for runtime service selection
const serviceInstances: Record<string, <T>(config: AxiosRequestConfig) => Promise<T>> = {
  'visitor-api': visitorApiInstance,
  notifications: notificationsInstance,
  workflow: workflowInstance,
  files: filesInstance,
  iprotect: iprotectInstance,
  transconnect: transconnectInstance,
};

/**
 * Get a service instance by name for dynamic API calls
 * @param serviceName - The name of the service (e.g., 'visitor-api', 'workflow')
 * @returns The axios instance function for that service, or undefined if not found
 */
export function getServiceInstance(serviceName: string): (<T>(config: AxiosRequestConfig) => Promise<T>) | undefined {
  return serviceInstances[serviceName.toLowerCase()];
}

/**
 * Fetch data from a service by name dynamically
 * @param serviceName - The name of the service (e.g., 'visitor-api', 'workflow')
 * @param url - The URL path to fetch (e.g., '/api/v1/items')
 * @returns Promise with the response data
 */
export async function fetchFromService<T>(serviceName: string, url: string): Promise<T> {
  const instance = getServiceInstance(serviceName);
  if (!instance) {
    throw new Error(`Unknown service: ${serviceName}`);
  }
  return instance<T>({ url, method: 'GET' });
}
